/**
 * The development brain's autopilot, as the Arendusaju tab's engine strip: one
 * switch, when it last ran and runs next, "run one", and what it costs and
 * what it did behind the gear.
 *
 * ON THE BRAIN'S OWN TAB, above the companies it has looked at. It sat on the
 * scanner-leads page until R11, and only because of an accident of order: the
 * engine was built on 17 August and the brain got a page of its own on the 18th,
 * so on the day the card was written there was nowhere else to put it. The leads
 * list is still where the picker takes its TARGETS from (`auto/pick.ts` reads
 * `ArScanLead`), but that says where the input comes from, not where a person
 * goes to stop the engine or to see what it spent. Those are here, next to its
 * output.
 *
 * NOT MERGED WITH THE SCANNER'S STRIP EITHER, for a separate reason that still
 * holds: the two engines are bounded by different things. The scanner fetches
 * pages and is capped in sites per day; this one opens a real browser in
 * Cloudflare and thinks with a model, and is capped by ten minutes of browser
 * time a day for the entire platform. One switch next to two sets of numbers
 * that never move together helps nobody. They share only the strip's shape
 * (`prospecting/engines/EngineLine.tsx`); it was a card with its own number
 * tiles until plan step 5.
 *
 * The switch writes straight through: a stop button with a save step is not a
 * stop button, and this is the engine with a bill attached.
 *
 * State lives in `useBrainAutopilot.ts`; the form is in
 * `brain-autopilot/BrainAutopilotSettings.tsx` and the log in
 * `brain-autopilot/BrainAutopilotHistory.tsx`, split because the two halves are
 * edited for different reasons.
 */
import { useEffect, useState } from "react";
import { Brain } from "lucide-react";
import { formatWhen } from "../../../lib/siteScan";
import {
  runBrainAutopilot,
  saveBrainAutopilot,
  type BrainAutopilotSettings,
  type BrainAutopilotState,
} from "../../../lib/siteBrain";
import { useSignals } from "../../shell/SignalsProvider";
import { EngineLine, EngineStat } from "../prospecting/engines";
import type { T } from "../site-scanner/types";
import { BrainAutopilotHistory } from "./brain-autopilot/BrainAutopilotHistory";
import { BrainAutopilotSettings as SettingsForm } from "./brain-autopilot/BrainAutopilotSettings";

/** How long "Run now" keeps the spinner before re-reading the batch's status. */
const START_SETTLE_MS = 1500;

/** `browser-budget` -> `browserBudget`, so a server enum can address a key. */
const noteKey = (note: string): string => note.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());

export function BrainAutopilotPanel({
  state,
  onChanged,
  canManage,
  t,
}: {
  state: BrainAutopilotState | null;
  onChanged: () => void;
  canManage: boolean;
  t: T;
}) {
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const { refresh: refreshSignals } = useSignals();

  // The rail's jobs card polls once a minute and a manual batch of three sites
  // is over well inside that, so the one place in the interface that exists to
  // say "something is running in the background" would reliably miss the whole
  // batch. Every time this strip learns the engine started or stopped, the rail
  // is told as well.
  const busy = Boolean(state?.busy);
  useEffect(() => {
    refreshSignals();
  }, [busy, refreshSignals]);

  if (!state) return null;
  const s = state.settings;
  // Guarded: one 200 response with a thinner body than expected used to throw
  // in the scanner's twin of this file and take the whole page down with it.
  const last = state.runs?.[0] ?? null;
  const runs = state.runs ?? [];

  const patch = async (change: Partial<BrainAutopilotSettings>) => {
    setSaving(true);
    try {
      await saveBrainAutopilot(change);
      onChanged();
    } finally {
      setSaving(false);
    }
  };

  const start = async () => {
    setStarting(true);
    try {
      await runBrainAutopilot(1);
      onChanged();
      // The POST answers before the batch has claimed its first domain, so the
      // refresh above can still read `busy: false` and the strip sits there
      // looking as though the button did nothing. One delayed follow-up hands
      // the job to the list's poll; a batch that finished instantly because it
      // had no browser time left is already on the strip by then, with its
      // reason on the row.
      await new Promise((resolve) => setTimeout(resolve, START_SETTLE_MS));
      onChanged();
      refreshSignals();
    } finally {
      setStarting(false);
    }
  };

  const lastRun = last
    ? last.note
      ? `${t("leads.brainAuto.lastRunIdle", { when: formatWhen(last.startedAt) })} ${t(
          `leads.brainAuto.note.${noteKey(last.note)}`,
        )}`
      : t("leads.brainAuto.lastRun", {
          when: formatWhen(last.startedAt),
          done: last.done,
          failed: last.failed,
          seconds: Math.round(last.browserMs / 1000),
        })
    : t("leads.brainAuto.neverRan");
  const when = state.nextRunAt
    ? `${lastRun} · ${t("leads.brainAuto.nextRun", { when: formatWhen(state.nextRunAt) })}`
    : lastRun;

  return (
    <EngineLine
      icon={Brain}
      title={t("leads.brainAuto.title")}
      status={
        state.busy
          ? t("leads.brainAuto.running")
          : s.enabled
            ? t("leads.brainAuto.on", { minutes: s.intervalMinutes, batch: s.batchSize })
            : t("leads.brainAuto.off")
      }
      when={when}
      enabled={s.enabled}
      busy={state.busy}
      canManage={canManage}
      saving={saving}
      onToggle={(checked) => void patch({ enabled: checked })}
      run={{
        label: t(state.busy ? "leads.brainAuto.busy" : "leads.brainAuto.runNow"),
        pending: starting || state.busy,
        onRun: () => void start(),
      }}
      settingsLabel={t("leads.brainAuto.settings")}
    >
      {/* Companies waiting for a follow-up somebody agreed to, and the
          screenshots due for clearing. The first is `hot`: a promise to ring
          back is the most perishable thing in this funnel. */}
      <div className="crm-pstats pt-3">
        <EngineStat label={t("leads.brainAuto.stat.due")} value={state.stats.due} hot />
        <EngineStat label={t("leads.brainAuto.stat.purgeDue")} value={state.stats.purgeDue} />
      </div>
      <SettingsForm settings={s} canManage={canManage} saving={saving} onPatch={(c) => void patch(c)} t={t} />
      <BrainAutopilotHistory runs={runs} t={t} />
    </EngineLine>
  );
}
